/**
 * ReviewSessionDetail — AI-enhanced findings review workflow
 *
 * Three-panel layout with collapsible panels:
 *   Left:   Findings list with severity/decision filter dropdowns (collapsible)
 *   Center: Selected finding detail with decision buttons
 *   Right:  Side-panel with Activity & Changes tabs (toggle)
 *
 * Real-time updates via PubSub for live collaboration.
 * Document viewer opens via GlobalModal.
 *
 * URL: /reviews/:reviewUuid
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';

import DetailFrame from '../shared/Frames/DetailFrame';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import Textarea from '../shared/TailwindComponents/Textarea';
import LogoSpinner from '../shared/LogoSpinner';
import ProfileImage from '../Header/ProfileImage';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiCheckCircleLight,
  PiXCircleLight,
  PiPencilSimpleLight,
  PiArrowUpRightLight,
  PiWarningCircleLight,
  PiFunnelLight,
  PiFileTextLight,
  PiSpinnerGapLight,
  PiShareNetworkLight,
  PiClockCounterClockwiseLight,
  PiArrowsClockwiseLight,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiEyeLight,
  PiArrowSquareOutLight,
  PiTrashLight,
  PiCaretDownLight,
  PiSealCheckLight,
  PiDownloadSimpleLight,
  PiLockKeyLight,
  PiArrowCounterClockwiseLight,
  PiQuestionLight,
  PiFlagCheckeredLight,
} from 'react-icons/pi';

import {
  fetchReviewSession,
  fetchFindings,
  updateFindingDecision,
  addFindingNote,
  updateFindingNote,
  deleteFindingNote,
  undoFindingDecision,
  bulkUpdateFindingDecisions,
  fetchReviewDetail,
  fetchCollaborators,
  addCollaborator as addReviewCollaborator,
  removeCollaborator as removeReviewCollaborator,
  updateCollaborator as updateReviewCollaborator,
  startReReview,
  deleteReviewSession,
  fetchSignOffStatus,
  submitSignOff,
  reopenReview,
  downloadFinalDocuments,
  completeReview,
} from '../../store/actions/Review/ReviewActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  Finding,
  FindingSeverity,
  FindingDecision,
  ReviewSessionDetail as SessionDetailType,
  SignOffStatus,
} from '../../types/review';
import { useReviewSessionPubSub } from './useReviewSessionPubSub';
import ReviewAuditLog from './ReviewAuditLog';
import InlineDiff from './InlineDiff';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, reviewSessionTourSteps } from '../shared/FeatureTour';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const severityColors: Record<FindingSeverity, BadgeColor> = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow',
  info: 'blue',
};

const severityLabels: Record<FindingSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};

const decisionColors: Record<string, BadgeColor> = {
  accepted: 'green',
  rejected: 'red',
  modified: 'orange',
  escalated: 'purple',
};

const severityOrder: FindingSeverity[] = ['critical', 'major', 'minor', 'info'];

/** Convert snake_case or slug text into Title Case for display.
 *  Uses (^|\s) instead of \b to avoid treating non-ASCII characters
 *  (ä, ö, å, é …) as word boundaries. */
const formatLabel = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/** Convert a UTC ISO string to local browser time. */
const formatUtcToLocal = (
  utcString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string => {
  if (!utcString) return '';
  const date = new Date(utcString.endsWith('Z') ? utcString : utcString + 'Z');
  if (isNaN(date.getTime())) return utcString;
  return date.toLocaleString(undefined, options ?? {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── Component ───────────────────────────────────────────────────────────────

const ReviewSessionDetail: React.FC = () => {
  const { reviewUuid } = useParams<{ reviewUuid: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const findingsListRef = useRef<HTMLDivElement>(null);

  // Redux state
  const currentSession = useSelector((s: RootState) => s.review.currentSession);
  const currentReview = useSelector((s: RootState) => s.review.currentReview);
  const findings = useSelector((s: RootState) => s.review.findings);
  const isLoadingSession = useSelector((s: RootState) => s.review.isLoadingSession);
  const isLoadingFindings = useSelector((s: RootState) => s.review.isLoadingFindings);
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const signOffStatus = useSelector((s: RootState) => s.review.signOffStatus);
  const isLoadingSignOff = useSelector((s: RootState) => s.review.isLoadingSignOff);

  // Local state
  const [selectedFindingUuid, setSelectedFindingUuid] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<FindingSeverity | 'all'>('all');
  const [decisionFilter, setDecisionFilter] = useState<string | 'all'>('all');
  const [decisionNotes, setDecisionNotes] = useState('');
  const [modifiedText, setModifiedText] = useState('');
  const [showModifyInput, setShowModifyInput] = useState(false);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [editingNoteUuid, setEditingNoteUuid] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');

  // Panel layout state
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const prevFindingsCountRef = useRef(0);

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'review-session',
    steps: reviewSessionTourSteps,
    readyToShow: !isLoadingSession && !!currentSession && findings.length > 0,
    autoStartDelay: 600,
  });

  // ─── Finalization computed values ──────────────────────────────────────
  const isFinalized = currentSession?.status === 'finalized';
  const isSignOffPending = currentSession?.status === 'pending_sign_off';
  const decisionsLocked = isFinalized;
  const allDecided = currentSession?.decision_progress
    ? currentSession.decision_progress.pending === 0 && currentSession.decision_progress.total > 0
    : false;

  // Check if current user can sign off (is a required signer and hasn't signed yet)
  const canSignOff = useMemo(() => {
    if (!signOffStatus?.signers || !userInfo?.UserUID) return false;
    const mySigner = signOffStatus.signers.find(
      (s) => s.user_uuid === userInfo.UserUID
    );
    return mySigner ? !mySigner.signed_off : false;
  }, [signOffStatus, userInfo?.UserUID]);

  const hasSignedOff = useMemo(() => {
    if (!signOffStatus?.signers || !userInfo?.UserUID) return false;
    const mySigner = signOffStatus.signers.find(
      (s) => s.user_uuid === userInfo.UserUID
    );
    return mySigner?.signed_off ?? false;
  }, [signOffStatus, userInfo?.UserUID]);

  // ─── Real-time PubSub connection ─────────────────────────────────────
  // Always connect for live collaboration (not just during processing)
  const isProcessing = currentSession?.status === 'processing' || currentSession?.status === 'pending';
  const playbookUuid = currentSession?.playbook_uuid ?? undefined;
  useReviewSessionPubSub(reviewUuid, playbookUuid, true);

  // ─── Dynamic height offset for banners above scrollable panels ────────
  // Only one status banner shows at a time; compute extra px consumed.
  const bannerOffset = useMemo(() => {
    let offset = 0;
    if (isProcessing) offset += 40;
    if (isFinalized) offset += 48;
    else if (isSignOffPending) offset += 52;
    else if (allDecided && (currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors')) offset += 44;
    return offset;
  }, [isProcessing, isFinalized, isSignOffPending, allDecided, currentSession?.status]);

  // ─── Data loading ──────────────────────────────────────────────────────

  // Clear stale data when navigating to a different session
  useEffect(() => {
    dispatch({ type: 'REVIEW_SET_SESSION', payload: null });
    dispatch({ type: 'REVIEW_SET_FINDINGS', payload: { findings: [], total: 0, page: 1 } });
    dispatch({ type: 'REVIEW_SET_SIGN_OFF_STATUS', payload: null });
    setSelectedFindingUuid(null);
    setDecisionNotes('');
    setModifiedText('');
    setShowModifyInput(false);
  }, [dispatch, reviewUuid]);

  useEffect(() => {
    if (reviewUuid) {
      dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
      dispatch(fetchFindings(reviewUuid, reviewUuid, { page: 1, page_size: 200 }) as any);
    }
  }, [dispatch, reviewUuid]);

  // Load parent playbook detail once we know the playbook UUID
  useEffect(() => {
    if (playbookUuid && !currentReview) {
      dispatch(fetchReviewDetail(playbookUuid) as any);
    }
  }, [dispatch, playbookUuid, currentReview]);

  // Fetch sign-off status when session is in sign-off or finalized state
  useEffect(() => {
    if (reviewUuid && currentSession && ['pending_sign_off', 'finalized'].includes(currentSession.status)) {
      dispatch(fetchSignOffStatus(reviewUuid) as any);
    }
  }, [dispatch, reviewUuid, currentSession?.status]);

  // Auto-select first finding
  useEffect(() => {
    if (findings.length > 0 && !selectedFindingUuid) {
      setSelectedFindingUuid(findings[0].uuid);
    }
  }, [findings, selectedFindingUuid]);

  // Auto-scroll to new findings as they stream in
  useEffect(() => {
    if (findings.length > prevFindingsCountRef.current && isProcessing) {
      const listEl = findingsListRef.current;
      if (listEl) {
        requestAnimationFrame(() => {
          listEl.scrollTop = listEl.scrollHeight;
        });
      }
    }
    prevFindingsCountRef.current = findings.length;
  }, [findings.length, isProcessing]);

  // ─── Filtered findings ─────────────────────────────────────────────────

  const filteredFindings = useMemo(() => {
    let result = findings;
    if (severityFilter !== 'all') {
      result = result.filter((f) => f.severity === severityFilter);
    }
    if (decisionFilter !== 'all') {
      if (decisionFilter === 'pending') {
        result = result.filter((f) => !f.decision);
      } else {
        result = result.filter((f) => f.decision === decisionFilter);
      }
    }
    return result;
  }, [findings, severityFilter, decisionFilter]);

  const selectedFinding = useMemo(
    () => findings.find((f) => f.uuid === selectedFindingUuid) || null,
    [findings, selectedFindingUuid],
  );

  // Sync note editor with the currently-selected finding's saved notes
  useEffect(() => {
    setDecisionNotes('');
    setEditingNoteUuid(null);
    setEditingContent('');
  }, [selectedFindingUuid]);

  // Count pending findings in the current filtered view (for bulk actions)
  const pendingFilteredFindings = useMemo(
    () => filteredFindings.filter((f) => !f.decision),
    [filteredFindings],
  );

  // ─── Decision handling ─────────────────────────────────────────────────

  const handleDecision = useCallback(async (decision: NonNullable<FindingDecision>) => {
    if (!selectedFinding || !reviewUuid) return;

    const payload: any = {
      decision,
      notes: decisionNotes || undefined,
    };

    // Include modified text for 'modified' decisions
    if (decision === 'modified' && modifiedText.trim()) {
      payload.modified_text = modifiedText.trim();
    }

    await dispatch(
      updateFindingDecision(reviewUuid, reviewUuid, selectedFinding.uuid, payload) as any,
    );

    setDecisionNotes('');
    setModifiedText('');
    setShowModifyInput(false);

    // Advance to next undecided finding
    const currentIdx = filteredFindings.findIndex((f) => f.uuid === selectedFinding.uuid);
    const nextUndecided = filteredFindings.find(
      (f, i) => i > currentIdx && !f.decision
    );
    if (nextUndecided) {
      setSelectedFindingUuid(nextUndecided.uuid);
    }
  }, [dispatch, reviewUuid, selectedFinding, decisionNotes, modifiedText, filteredFindings]);

  // ─── Undo decision ──────────────────────────────────────────────────────

  const handleUndoDecision = useCallback(async () => {
    if (!selectedFinding || !reviewUuid || !selectedFinding.decision) return;
    await dispatch(undoFindingDecision(reviewUuid, selectedFinding.uuid) as any);
  }, [dispatch, reviewUuid, selectedFinding]);

  // ─── Finding notes (multi-note thread) ──────────────────────────────────

  const handleAddNote = useCallback(async () => {
    if (!selectedFinding || !reviewUuid || !decisionNotes.trim()) return;
    setIsSavingNotes(true);
    try {
      const result = await dispatch(
        addFindingNote(reviewUuid, selectedFinding.uuid, decisionNotes.trim()) as any,
      );
      if (result) setDecisionNotes('');
    } finally {
      setIsSavingNotes(false);
    }
  }, [dispatch, reviewUuid, selectedFinding, decisionNotes]);

  const handleUpdateNote = useCallback(
    async (noteUuid: string, content: string) => {
      if (!selectedFinding || !reviewUuid || !content.trim()) return false;
      setIsSavingNotes(true);
      try {
        const result = await dispatch(
          updateFindingNote(reviewUuid, selectedFinding.uuid, noteUuid, content.trim()) as any,
        );
        return !!result;
      } finally {
        setIsSavingNotes(false);
      }
    },
    [dispatch, reviewUuid, selectedFinding],
  );

  const handleDeleteNote = useCallback(
    async (noteUuid: string) => {
      if (!selectedFinding || !reviewUuid) return;
      setIsSavingNotes(true);
      try {
        await dispatch(deleteFindingNote(reviewUuid, selectedFinding.uuid, noteUuid) as any);
      } finally {
        setIsSavingNotes(false);
      }
    },
    [dispatch, reviewUuid, selectedFinding],
  );

  // ─── Bulk accept/reject handlers ─────────────────────────────────────────────

  const handleBulkDecision = useCallback((decision: 'accepted' | 'rejected') => {
    if (!reviewUuid || pendingFilteredFindings.length === 0) return;

    const count = pendingFilteredFindings.length;
    const uuids = pendingFilteredFindings.map((f) => f.uuid);
    const label = decision === 'accepted' ? t('accept') : t('reject');
    const Label = decision === 'accepted' ? t('Accept') : t('Reject');

    // Build context about what's being bulk-acted on
    const filterDesc = severityFilter !== 'all'
      ? ` ${severityLabels[severityFilter as FindingSeverity]?.toLowerCase() || severityFilter}`
      : '';

    const confirmMessage = t(
      'Are you sure you want to {{label}} {{count}}{{filter}} pending findings? This action will apply to all undecided findings in the current view.',
      { label, count, filter: filterDesc },
    );

    dispatch(
      showModal({
        type: 'confirmation',
        message: `${Label} ${count} Findings`,
        content: confirmMessage,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(bulkUpdateFindingDecisions(reviewUuid, decision, uuids) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, pendingFilteredFindings, severityFilter, t]);

  // ─── Open document viewer ──────────────────────────────────────────────

  const handleOpenDocument = useCallback((docId: number | null, docName: string | null) => {
    if (!docId || !reviewUuid) return;
    dispatch(showModal({
      showModal: true,
      type: 'reviewDocumentViewer' as any,
      message: docName || 'Document',
      icon: <PiFileTextLight className="w-5 h-5 text-gray-500" />,
      sessionUuid: reviewUuid,
      documentId: docId,
      documentName: docName || 'Document',
    }));
  }, [dispatch, reviewUuid]);

  // ─── Share handler ─────────────────────────────────────────────────────

  const handleShare = useCallback(() => {
    if (!reviewUuid) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      width: '50%',
      message: t('Manage Access'),
      resourceUuid: reviewUuid,
      resourceType: 'review_session',
      collaborators: currentSession?.collaborators || [],
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addReviewCollaborator(uuid, { collaborator_id: userUuid, access: access as any }) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeReviewCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateReviewCollaborator(uuid, collabUuid, { access: access as any }) as any);
      },
      onUpdate: async () => {
        await dispatch(fetchCollaborators(reviewUuid) as any);
        dispatch(fetchReviewSession(reviewUuid, reviewUuid) as any);
      },
    }));
  }, [dispatch, reviewUuid, currentSession?.collaborators, userInfo?.UserUID, t]);

  // ─── Navigate to finding from audit log ─────────────────────────────────

  const handleFindingSelectFromLog = useCallback((findingUuid: string) => {
    setSelectedFindingUuid(findingUuid);
    setDecisionNotes('');
    setShowModifyInput(false);
    setModifiedText('');
  }, []);

  // ─── Re-review handler ─────────────────────────────────────────────────

  const handleReReview = useCallback(async () => {
    if (!reviewUuid) return;
    const result = await dispatch(startReReview(reviewUuid, { files: [] }) as any);
    if (result?.session_uuid) {
      history.push(`/reviews/${result.session_uuid}`);
    }
  }, [dispatch, reviewUuid, history]);

  // ─── Sign-off handler ──────────────────────────────────────────────────

  const handleSignOff = useCallback(async () => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Sign off on this review?'),
        content: t('This confirms that you have reviewed all findings and agree with the decisions made. Once all required signers have signed off, the review will be finalized.'),
        icon: <PiSealCheckLight className='w-4 h-4' />,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(submitSignOff(reviewUuid) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  // ─── Complete review handler ────────────────────────────────────────────

  const handleCompleteReview = useCallback(() => {
    if (!reviewUuid || !currentSession) return;

    const progress = currentSession.decision_progress;
    const totalFindings = progress?.total ?? findings.length;
    const decidedFindings = progress?.decided ?? findings.filter((f) => f.decision).length;
    const pendingFindings = totalFindings - decidedFindings;
    const allDecided = pendingFindings === 0;

    // Build severity breakdown of undecided findings
    const undecidedBySeverity = findings
      .filter((f) => !f.decision)
      .reduce((acc, f) => {
        acc[f.severity] = (acc[f.severity] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    const severityLine = Object.entries(undecidedBySeverity)
      .map(([sev, count]) => `${count} ${sev}`)
      .join(', ');

    const confirmMessage = allDecided
      ? t('All {{total}} findings have been reviewed. Mark this review as complete and move to the sign-off stage?', { total: totalFindings })
      : t('You have reviewed {{decided}} of {{total}} findings. {{pending}} findings are still undecided ({{severity}}). Are you sure you want to mark this review as complete? Undecided findings will be treated as accepted risks.', {
          decided: decidedFindings,
          total: totalFindings,
          pending: pendingFindings,
          severity: severityLine,
        });

    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Complete Review'),
        icon: <PiFlagCheckeredLight />,
        content: confirmMessage,
        showModal: true,
        onConfirm: async () => {
          dispatch(hideModal());
          await dispatch(completeReview(reviewUuid) as any);
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, currentSession, findings, t]);

  // ─── Reopen handler ────────────────────────────────────────────────────

  const handleReopenReview = useCallback(async () => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Reopen this review?'),
        content: t('All sign-offs will be cleared and collaborators will need to sign off again.'),
        icon: <PiArrowCounterClockwiseLight className='w-4 h-4' />,
        showModal: true,
        onConfirm: async () => {
          await dispatch(reopenReview(reviewUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  // ─── Download final handler ────────────────────────────────────────────

  const handleDownloadFinal = useCallback(async () => {
    if (!reviewUuid) return;
    await dispatch(downloadFinalDocuments(reviewUuid) as any);
  }, [dispatch, reviewUuid]);

  // ─── Delete handler ─────────────────────────────────────────────────────

  const handleDelete = useCallback(() => {
    if (!reviewUuid) return;
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Delete Review'),
        icon: <PiTrashLight className='text-red-600 w-4 h-4' />,
        content: t('Are you sure you want to delete this review session? This action can be undone.'),
        showModal: true,
        onConfirm: async () => {
          const success = await dispatch(deleteReviewSession(reviewUuid) as any);
          if (success) {
            dispatch(hideModal());
            history.push('/reviews');
          }
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, history, t]);

  // ─── Help guide modal ──────────────────────────────────────────────────

  const handleOpenHelp = useCallback(() => {
    dispatch(showModal({
      showModal: true,
      type: 'reviewHelp' as any,
      message: t('How Document Review Works'),
      icon: <PiQuestionLight className="w-5 h-5 text-gray-500" />,
    }));
  }, [dispatch, t]);

  // ─── Header dropdown actions ────────────────────────────────────────────

  const isOwner = useMemo(() => {
    if (!userInfo?.UserUID || !currentSession?.collaborators) return false;
    return currentSession.collaborators.some(
      (c) => c.user_id === userInfo.UserUID && c.access === 'admin',
    );
  }, [userInfo?.UserUID, currentSession?.collaborators]);

  const headerDropdownActions = useMemo(() => {
    const actions: Array<{
      label: string;
      onClick: () => void;
      icon?: React.ReactElement;
      variant?: 'primary' | 'neutral' | 'outline' | 'destructive';
    }> = [
      {
        label: t('Activity'),
        icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
        onClick: () => setActivityOpen(!activityOpen),
      },
      {
        label: t('Manage Access'),
        icon: <PiShareNetworkLight className="w-4 h-4" />,
        onClick: handleShare,
      },
    ];

    // if (currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors') {
    //   actions.push({
    //     label: t('Re-review'),
    //     icon: <PiArrowsClockwiseLight className="w-4 h-4" />,
    //     onClick: handleReReview,
    //   });
    // }

    if (isFinalized && isOwner) {
      actions.push({
        label: t('Reopen Review'),
        icon: <PiArrowCounterClockwiseLight className="w-4 h-4" />,
        onClick: handleReopenReview,
      });
    }

    if (isOwner) {
      actions.push({
        label: t('Delete'),
        icon: <PiTrashLight className="w-4 h-4" />,
        onClick: handleDelete,
        variant: 'destructive',
      });
    }

    return actions;
  }, [t, handleShare, handleReReview, handleReopenReview, handleDelete, currentSession?.status, isOwner, isFinalized, activityOpen]);

  // ─── Breadcrumbs ───────────────────────────────────────────────────────

  const breadcrumbs = useMemo(() => {
    const sessionName = currentSession?.name || currentReview?.name || t('Review');

    return [
      { name: t('Reviews'), href: '/reviews', current: false },
      { name: sessionName, href: '', current: true },
    ];
  }, [currentSession?.name, currentReview, t]);
  // ─── Collaborators for header ──────────────────────────────────────

  const headerCollaborators = useMemo(() => {
    if (!currentSession?.collaborators) return [];
    return currentSession.collaborators.map((c) => ({
      user_uuid: c.user_id,
      name: c.name,
      initials: c.initials,
      access: c.access || 'view',
      email: c.email || null,
      avatar_url: c.avatar_url || null,
    }));
  }, [currentSession?.collaborators]);
  // ─── Stats ─────────────────────────────────────────────────────────────

  const decidedCount = useMemo(
    () => findings.filter((f) => f.decision).length,
    [findings],
  );

  // ─── Document progress ────────────────────────────────────────────────

  const documentsCompleted = useMemo(() => {
    if (!currentSession?.documents) return 0;
    return currentSession.documents.filter(
      (d) => d.status === 'completed' || d.status === 'failed',
    ).length;
  }, [currentSession?.documents]);

  const documentsTotal = currentSession?.document_count || 0;
  const progressPercent = documentsTotal > 0 ? Math.round((documentsCompleted / documentsTotal) * 100) : 0;

  // ─── Loading ───────────────────────────────────────────────────────────

  if (isLoadingSession && !currentSession) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} actions={[]}>
        <div className="flex items-center justify-center h-64">
          <LogoSpinner size="medium" />
        </div>
      </DetailFrame>
    );
  }

  return (<>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      actions={[]}
      dropdownActions={headerDropdownActions}
      collaborators={headerCollaborators}
      additionalHeaderElements={
        <>
          <ProjectBadges resourceType="review_session" resourceUuid={reviewUuid} />
          <PendingAccessRequests resourceType="review_session" resourceUuid={reviewUuid} />
          <Button
            onClick={handleOpenHelp}
            title={t('How does this work?')}
            size='small'
            className='py-1.5'
            variant='outline'
          >
            <PiQuestionLight className="w-3.5 h-3.5" />
          </Button>
        </>
      }
    >
      {/* Processing indicator — subtle inline bar */}
      {isProcessing && (
        <div className="flex-shrink-0 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800 px-5 py-2">
          <div className="flex items-center gap-3">
            <PiSpinnerGapLight className="w-3.5 h-3.5 text-blue-400 animate-spin flex-shrink-0" />
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {t('Analyzing')} {documentsCompleted}/{documentsTotal} {t('documents')}
            </span>
            <div className="flex-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden max-w-[180px]">
              <div
                className="h-full bg-blue-400/70 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
              {findings.length} {t('findings')}
            </span>
          </div>
        </div>
      )}

      {/* Finalized banner */}
      {isFinalized && (
        <div className="flex-shrink-0 border-b border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <PiSealCheckLight className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              <div>
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  {t('Review Finalized')}
                </span>
                {currentSession?.finalized_at && (
                  <span className="text-xs text-emerald-600/70 dark:text-emerald-400/70 ml-2">
                    {formatUtcToLocal(currentSession.finalized_at)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleDownloadFinal}
                variant='primary'
                size='small'
                icon={<PiDownloadSimpleLight className="w-3.5 h-3.5" />}
              >
                {t('Download Final')}
              </Button>
              {isOwner && (
                <Button
                  onClick={handleReopenReview}
                  variant='outline'
                  size='small'
                  icon={<PiArrowCounterClockwiseLight className="w-3.5 h-3.5" />}
                >
                  {t('Reopen')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* All decided banner — shown when all findings reviewed but review not yet completed */}
      {allDecided && !isProcessing && !isSignOffPending && !isFinalized && (currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors') && (
        <div className="flex-shrink-0 border-b border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-6 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PiCheckCircleLight className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                {t('All findings reviewed — ready to complete')}
              </span>
            </div>
            <Button
              onClick={handleCompleteReview}
              variant='primary'
              size='small'
              icon={<PiFlagCheckeredLight className="w-3.5 h-3.5" />}
            >
              {t('Complete Review')}
            </Button>
          </div>
        </div>
      )}

      {/* Pending sign-off banner */}
      {isSignOffPending && !isFinalized && (
        <div className="flex-shrink-0 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <PiSealCheckLight className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <div>
                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  {t('All findings reviewed — awaiting sign-off')}
                </span>
                {signOffStatus && (
                  <span className="text-xs text-amber-600/70 dark:text-amber-400/70 ml-2">
                    {signOffStatus.signed_count}/{signOffStatus.required_count} {t('signed')}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Sign-off progress — profile images with status ring */}
              {signOffStatus?.signers && (
                <div className="flex items-center -space-x-1.5 mr-2">
                  {signOffStatus.signers.map((signer) => {
                    // Cross-reference with collaborators for avatar/email/initials
                    const collab = currentSession?.collaborators?.find((c) => c.user_id === signer.user_uuid);
                    const initials = collab?.initials || signer.user_name?.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
                    return (
                      <div
                        key={signer.user_uuid}
                        className={clsx(
                          'rounded-full border-2',
                          signer.signed_off
                            ? 'border-emerald-400'
                            : 'border-gray-300 dark:border-gray-600 opacity-60',
                        )}
                        title={`${signer.user_name} — ${signer.signed_off ? 'Signed off' : 'Pending'}`}
                      >
                        <ProfileImage
                          profilePicture={collab?.avatar_url || null}
                          email={collab?.email || null}
                          initials={initials}
                          name={signer.user_name}
                          size="w-6 h-6"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {canSignOff && (
                <Button
                  onClick={handleSignOff}
                  variant='primary'
                  size='small'
                  icon={<PiSealCheckLight className="w-3.5 h-3.5" />}
                >
                  {t('Sign Off')}
                </Button>
              )}
              {hasSignedOff && (
                <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                  <PiCheckCircleLight className="w-4 h-4" />
                  {t('You signed off')}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Session summary bar */}
      <div className="flex-shrink-0 border-b border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800 px-5 py-3" data-tour-id="review-session-summary-bar">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            {/* Risk score — hero metric */}
            {currentSession?.overall_risk_score !== null && currentSession?.overall_risk_score !== undefined && (
              <div className="flex items-center gap-1.5">
                <span className={clsx(
                  'text-xl tabular-nums',
                  (currentSession.overall_risk_score ?? 0) >= 7 ? 'text-red-500' :
                  (currentSession.overall_risk_score ?? 0) >= 4 ? 'text-amber-500' : 'text-emerald-500',
                )}>
                  {Number(currentSession.overall_risk_score).toFixed(1)}
                </span>
                <span className="text-xs text-gray-400 leading-tight">{t('risk')} {t('score')}</span>
              </div>
            )}

            {/* Divider */}
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />

            {/* Severity summary — compact dot indicators */}
            {currentSession && (
              <div className="flex items-center gap-3">
                {currentSession.critical_count > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{currentSession.critical_count}</span>
                  </div>
                )}
                {currentSession.major_count > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-orange-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{currentSession.major_count}</span>
                  </div>
                )}
                {currentSession.minor_count > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{currentSession.minor_count}</span>
                  </div>
                )}
                {currentSession.info_count > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">{currentSession.info_count}</span>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-700" />

            {/* Review progress */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {currentSession?.decision_progress
                  ? `${currentSession.decision_progress.decided}/${currentSession.decision_progress.total}`
                  : `${decidedCount}/${findings.length}`
                } {t('reviewed')}
              </span>
              {/* Mini progress bar */}
              {findings.length > 0 && (
                <div className="w-16 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-400 rounded-full transition-all duration-300"
                    style={{ width: `${Math.round(((currentSession?.decision_progress?.decided ?? decidedCount) / (currentSession?.decision_progress?.total ?? findings.length)) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Complete Review button — shown when review is completed/completed_with_errors and not processing */}
            {(currentSession?.status === 'completed' || currentSession?.status === 'completed_with_errors') && !isProcessing && (
              <Button
                onClick={handleCompleteReview}
                variant='primary'
                size='small'
                icon={<PiFlagCheckeredLight className="w-4 h-4" />}
              >
                {t('Complete Review')}
              </Button>
            )}
            {/* View Document button — hidden during processing */}
            {!isProcessing && currentSession?.documents && currentSession.documents.length > 0 && (
              currentSession.documents.length === 1 ? (
                <Button
                  onClick={() => {
                    const doc = currentSession.documents[0];
                    handleOpenDocument(doc.id, doc.file_name);
                  }}
                  variant='outline'
                  size='small'
                  icon={<PiEyeLight className="w-4 h-4" />}
                  data-tour-id="review-session-view-doc"
                >
                  {t('View Document')}
                </Button>
              ) : (
                <Menu as="div" className="relative" data-tour-id="review-session-view-doc">
                  <Menu.Button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md transition-colors">
                    <PiEyeLight className="w-4 h-4" />
                    {t('View Document')}
                    <PiCaretDownLight className="w-3 h-3" />
                  </Menu.Button>
                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-50 mt-1 w-64 origin-top-right rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 focus:outline-none py-1">
                      {currentSession.documents.map((doc) => (
                        <Menu.Item key={doc.id}>
                          {({ active }) => (
                            <button
                              onClick={() => handleOpenDocument(doc.id, doc.file_name)}
                              className={clsx(
                                'flex items-center gap-2 w-full px-3 py-2 text-sm text-left',
                                active ? 'bg-gray-50 dark:bg-gray-700' : '',
                              )}
                            >
                              <PiFileTextLight className="w-4 h-4 flex-shrink-0 text-gray-400" />
                              <span className="truncate text-gray-700 dark:text-gray-300">{doc.file_name}</span>
                              <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                                {doc.file_type?.toUpperCase()}
                              </span>
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              )
            )}
            <Button
              onClick={() => setActivityOpen(!activityOpen)}
              variant='outline'
              size='small'
              icon={<PiClockCounterClockwiseLight className="w-4 h-4" />}
              data-tour-id="review-session-activity"
            >
              {t('Activity')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">

        {/* ─── Left panel: Findings list (collapsible) ──────────────── */}
        <div
          className={clsx(
            'flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden transition-all duration-200 bg-gray-50 dark:bg-gray-900',
            leftCollapsed ? 'w-10' : 'w-[340px]',
          )}
          data-tour-id="review-findings-panel"
        >
          {leftCollapsed ? (
            <button
              onClick={() => setLeftCollapsed(false)}
              className="flex-1 flex items-start py-4 justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title={t('Expand findings')}
            >
              <PiCaretRightLight className="w-4 h-4 text-gray-400" />
            </button>
          ) : (
            <>
              {/* Collapse button + filter bar */}
              <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-2 py-1.5 bg-gray-50 dark:bg-gray-900" data-tour-id="review-findings-filter-bar">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setLeftCollapsed(true)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title={t('Collapse')}
                  >
                    <PiCaretLeftLight className="w-3.5 h-3.5 text-gray-400" />
                  </button>

                  {/* Severity dropdown */}
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center gap-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <PiFunnelLight className="w-3.5 h-3.5 text-gray-400" />
                      <span>{severityFilter === 'all' ? t('Severity') : severityLabels[severityFilter as FindingSeverity]}</span>
                      <PiCaretDownLight className="w-3 h-3 text-gray-400" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Menu.Items className="absolute left-0 mt-1 w-44 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50 py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setSeverityFilter('all')}
                              className={clsx(
                                'w-full text-left px-3 py-1.5 text-xs flex items-center justify-between',
                                active ? 'bg-gray-100 dark:bg-gray-600' : '',
                                severityFilter === 'all' ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                              )}
                            >
                              <span>{t('All')}</span>
                              <span className="text-gray-400">{findings.length}</span>
                            </button>
                          )}
                        </Menu.Item>
                        {severityOrder.map((sev) => {
                          const count = findings.filter((f) => f.severity === sev).length;
                          if (count === 0) return null;
                          return (
                            <Menu.Item key={sev}>
                              {({ active }) => (
                                <button
                                  onClick={() => setSeverityFilter(sev)}
                                  className={clsx(
                                    'w-full text-left px-3 py-1.5 text-xs flex items-center justify-between',
                                    active ? 'bg-gray-100 dark:bg-gray-600' : '',
                                    severityFilter === sev ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                                  )}
                                >
                                  <div className="flex items-center gap-1.5">
                                    <Badge color={severityColors[sev]}>{severityLabels[sev]}</Badge>
                                  </div>
                                  <span className="text-gray-400">{count}</span>
                                </button>
                              )}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Items>
                    </Transition>
                  </Menu>

                  {/* Decision dropdown */}
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex items-center gap-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                      <span>{decisionFilter === 'all' ? t('Decision') : decisionFilter === 'pending' ? t('Pending') : formatLabel(decisionFilter)}</span>
                      <PiCaretDownLight className="w-3 h-3 text-gray-400" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Menu.Items className="absolute left-0 mt-1 w-44 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 z-50 py-1">
                        {(['all', 'pending', 'accepted', 'rejected', 'modified', 'escalated'] as const).map((df) => {
                          const count = df === 'all' ? findings.length :
                            df === 'pending' ? findings.filter(f => !f.decision).length :
                            findings.filter(f => f.decision === df).length;
                          if (count === 0 && df !== 'all' && df !== 'pending') return null;
                          return (
                            <Menu.Item key={df}>
                              {({ active }) => (
                                <button
                                  onClick={() => setDecisionFilter(df)}
                                  className={clsx(
                                    'w-full text-left px-3 py-1.5 text-xs flex items-center justify-between',
                                    active ? 'bg-gray-100 dark:bg-gray-600' : '',
                                    decisionFilter === df ? 'font-semibold text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-200',
                                  )}
                                >
                                  <span>{df === 'all' ? t('All') : df === 'pending' ? t('Pending') : formatLabel(df)}</span>
                                  <span className="text-gray-400">{count}</span>
                                </button>
                              )}
                            </Menu.Item>
                          );
                        })}
                      </Menu.Items>
                    </Transition>
                  </Menu>

                  {/* Active filter badges */}
                  {(severityFilter !== 'all' || decisionFilter !== 'all') && (
                    <button
                      onClick={() => { setSeverityFilter('all'); setDecisionFilter('all'); }}
                      className="px-1.5 py-0.5 rounded text-[11px] text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      title={t('Clear filters')}
                    >
                      {t('Clear')}
                    </button>
                  )}
                </div>
              </div>

              {/* Bulk action bar — shown when there are pending findings and review is not locked */}
              {!isProcessing && !decisionsLocked && pendingFilteredFindings.length > 0 && (
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-2 py-1.5 bg-gray-50 dark:bg-gray-900">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-gray-400 mr-0.5">
                      {pendingFilteredFindings.length} {t('pending')}
                    </span>
                    <button
                      onClick={() => handleBulkDecision('accepted')}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
                    >
                      <PiCheckCircleLight className="w-3 h-3" />
                      {t('Accept All')}
                    </button>
                    <button
                      onClick={() => handleBulkDecision('rejected')}
                      className="flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <PiXCircleLight className="w-3 h-3" />
                      {t('Reject All')}
                    </button>
                  </div>
                </div>
              )}

          {/* Findings list */}
          <div ref={findingsListRef} className="flex-1 overflow-y-auto custom-scrollbar-main" style={{ maxHeight: `calc(100vh - ${150 + bannerOffset}px)` }}>
            {isLoadingFindings && findings.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <LogoSpinner size="small" />
              </div>
            ) : filteredFindings.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-sm text-gray-400">
                {isProcessing ? (
                  <>
                    <PiSpinnerGapLight className="w-8 h-8 mb-2 animate-spin text-blue-400" />
                    {t('Waiting for findings...')}
                  </>
                ) : (
                  <>
                    <PiFileTextLight className="w-8 h-8 mb-2" />
                    {t('No findings')}
                  </>
                )}
              </div>
            ) : (
              filteredFindings.map((finding) => (
                <button
                  key={finding.uuid}
                  onClick={() => {
                    setSelectedFindingUuid(finding.uuid);
                    setDecisionNotes('');
                    setShowModifyInput(false);
                    setModifiedText('');
                  }}
                  className={clsx(
                    'w-full text-left px-4 py-3 transition-colors',
                    selectedFindingUuid === finding.uuid
                      ? 'bg-gray-50 dark:bg-gray-800/80 border-l-2 border-l-primary-500'
                      : 'hover:bg-gray-50/60 dark:hover:bg-gray-800/30 border-l-2 border-l-transparent',
                    'border-b border-gray-100/80 dark:border-gray-800/60',
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={clsx(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      finding.severity === 'critical' ? 'bg-red-500' :
                      finding.severity === 'major' ? 'bg-orange-400' :
                      finding.severity === 'minor' ? 'bg-yellow-400' : 'bg-blue-400',
                    )} />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 line-clamp-1">
                      {formatLabel(finding.clause_type)}
                    </span>
                    {finding.decision && (
                      <span className={clsx(
                        'ml-auto text-[11px] font-medium flex-shrink-0',
                        finding.decision === 'accepted' ? 'text-emerald-600 dark:text-emerald-400' :
                        finding.decision === 'rejected' ? 'text-red-500 dark:text-red-400' :
                        finding.decision === 'modified' ? 'text-amber-600 dark:text-amber-400' :
                        finding.decision === 'escalated' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400',
                      )}>
                        {formatLabel(finding.decision)}
                      </span>
                    )}
                  </div>
                  {finding.issue_description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 leading-relaxed pl-4">
                      {finding.issue_description}
                    </p>
                  )}
                  {finding.document_name && (
                    <p className="text-[11px] text-gray-400 mt-1 truncate pl-4">
                      {/* {finding.document_name} */}
                      {finding.section_reference && ` · ${finding.section_reference}`}
                    </p>
                  )}
                </button>
              ))
            )}
          </div>
            </>
          )}
        </div>

        {/* ─── Center panel: Finding detail ─────────────────────────── */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 bg-white dark:bg-gray-800" data-tour-id="review-finding-detail">
          {selectedFinding ? (
            <div className="flex-1 overflow-y-auto custom-scrollbar-main p-6 lg:p-8 space-y-6" style={{ maxHeight: `calc(100vh - ${100 + bannerOffset}px)` }}>
              {/* Header */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5 mb-2">
                    <span className={clsx(
                      'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide',
                      selectedFinding.severity === 'critical' ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                      selectedFinding.severity === 'major' ? 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400' :
                      selectedFinding.severity === 'minor' ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
                    )}>
                      {severityLabels[selectedFinding.severity]}
                    </span>
                    {selectedFinding.decision && (
                      <span className={clsx(
                        'text-xs font-medium',
                        selectedFinding.decision === 'accepted' ? 'text-emerald-600 dark:text-emerald-400' :
                        selectedFinding.decision === 'rejected' ? 'text-red-500 dark:text-red-400' :
                        selectedFinding.decision === 'modified' ? 'text-amber-600 dark:text-amber-400' :
                        selectedFinding.decision === 'escalated' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-400',
                      )}>
                        {formatLabel(selectedFinding.decision)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {formatLabel(selectedFinding.clause_type)}
                  </h3>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400 flex-shrink-0">
                  {selectedFinding.risk_score !== null && (
                    <span>Risk: {selectedFinding.risk_score}</span>
                  )}
                  {selectedFinding.confidence_score !== null && (
                    <span>Confidence: {Math.round((selectedFinding.confidence_score || 0) * 100)}%</span>
                  )}
                </div>
              </div>

              {/* Document reference — clickable to open viewer (disabled during processing) */}
              {selectedFinding.document_name && (
                <button
                  onClick={() => !isProcessing && handleOpenDocument(selectedFinding.document_id, selectedFinding.document_name)}
                  className={clsx(
                    'inline-flex items-center gap-2 text-sm transition-colors group',
                    isProcessing
                      ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300',
                  )}
                  disabled={isProcessing}
                >
                  <PiFileTextLight className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{selectedFinding.document_name}</span>
                  {selectedFinding.section_reference && (
                    <span className="text-gray-400">· {selectedFinding.section_reference}</span>
                  )}
                  <PiArrowSquareOutLight className="w-3.5 h-3.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-primary-500" />
                </button>
              )}

              {/* Original text */}
              {selectedFinding.original_text && (
                <div>
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    {t('Original Text')}
                  </h4>
                  <blockquote className="border-l-2 border-gray-200 dark:border-gray-700 pl-4 py-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {selectedFinding.original_text}
                  </blockquote>
                </div>
              )}

              {/* Issue description */}
              {selectedFinding.issue_description && (
                <div>
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <PiWarningCircleLight className="w-3.5 h-3.5" />
                    {t('Issue')}
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {selectedFinding.issue_description}
                  </p>
                </div>
              )}

              {/* Legal basis — between issue and suggestion */}
              {selectedFinding.legal_basis && (
                <div>
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    {t('Legal Basis')}
                  </h4>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50/50 dark:bg-gray-800/30">
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {typeof selectedFinding.legal_basis === 'string'
                        ? selectedFinding.legal_basis
                        : JSON.stringify(selectedFinding.legal_basis, null, 2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Notes — multi-note thread with compose + per-note edit/delete */}
              {!isProcessing && (
                <div>
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                    {t('Notes')}
                    {selectedFinding.notes && selectedFinding.notes.length > 0 && (
                      <span className="ml-1.5 text-gray-400 normal-case tracking-normal">
                        ({selectedFinding.notes.length})
                      </span>
                    )}
                  </h4>

                  {/* List of existing notes */}
                  {selectedFinding.notes && selectedFinding.notes.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {selectedFinding.notes.map((note) => {
                        const isEditing = editingNoteUuid === note.uuid;
                        const isOwnNote = !!note.is_mine;
                        const initials = (note.author_name || '?')
                          .split(' ')
                          .map((n) => n[0])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join('')
                          .toUpperCase();
                        return (
                          <div
                            key={note.uuid}
                            className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-amber-50/40 dark:bg-amber-900/10"
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="shrink-0 mt-0.5">
                                <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center text-[11px] font-medium">
                                  {initials}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2 text-xs">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">
                                      {note.author_name || t('Unknown')}
                                    </span>
                                    {note.created_at && (
                                      <span className="text-gray-400">
                                        {formatUtcToLocal(note.created_at)}
                                      </span>
                                    )}
                                    {note.updated_at && note.created_at && note.updated_at !== note.created_at && (
                                      <span className="text-gray-400 italic">
                                        {t('(edited)')}
                                      </span>
                                    )}
                                  </div>
                                  {!isEditing && isOwnNote && (
                                    <div className="flex items-center gap-0.5">
                                      <button
                                        onClick={() => {
                                          setEditingNoteUuid(note.uuid);
                                          setEditingContent(note.content || '');
                                        }}
                                        className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                                        title={t('Edit')}
                                      >
                                        <PiPencilSimpleLight className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNote(note.uuid)}
                                        disabled={isSavingNotes}
                                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors disabled:opacity-50"
                                        title={t('Delete')}
                                      >
                                        <PiTrashLight className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                {isEditing ? (
                                  <div>
                                    <Textarea
                                      id={`note-edit-${note.uuid}`}
                                      label=""
                                      value={editingContent}
                                      onChange={(e) => setEditingContent(e.target.value)}
                                      rows={3}
                                    />
                                    <div className="flex justify-end gap-2 mt-2">
                                      <Button
                                        variant="neutral"
                                        size="small"
                                        onClick={() => {
                                          setEditingNoteUuid(null);
                                          setEditingContent('');
                                        }}
                                        disabled={isSavingNotes}
                                      >
                                        {t('Cancel')}
                                      </Button>
                                      <Button
                                        variant="primary"
                                        size="small"
                                        onClick={async () => {
                                          const ok = await handleUpdateNote(note.uuid, editingContent);
                                          if (ok) {
                                            setEditingNoteUuid(null);
                                            setEditingContent('');
                                          }
                                        }}
                                        disabled={
                                          isSavingNotes ||
                                          !editingContent.trim() ||
                                          editingContent.trim() === (note.content || '').trim()
                                        }
                                      >
                                        {isSavingNotes ? t('Saving...') : t('Update')}
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                                    {note.content}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Always-visible composer for new notes */}
                  <div>
                    <Textarea
                      id="new-note"
                      label=""
                      placeholder={t('Add a note...')}
                      value={decisionNotes}
                      onChange={(e) => setDecisionNotes(e.target.value)}
                      rows={3}
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={handleAddNote}
                        disabled={isSavingNotes || !decisionNotes.trim()}
                      >
                        {isSavingNotes ? t('Saving...') : t('Add note')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Applied modification — shown once decision is 'modified' */}
              {selectedFinding.decision === 'modified' && selectedFinding.modified_text && (
                <div>
                  <h4 className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <PiPencilSimpleLight className="w-3.5 h-3.5" />
                    {t('Modification')}
                  </h4>
                  {selectedFinding.original_text ? (
                    <div className="bg-emerald-50/50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3">
                      <InlineDiff
                        original={selectedFinding.original_text}
                        modified={selectedFinding.modified_text}
                      />
                    </div>
                  ) : (
                    <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 text-sm text-emerald-800 dark:text-emerald-200">
                      {selectedFinding.modified_text}
                    </div>
                  )}
                  {selectedFinding.applied_at && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      {t('Applied')} {formatUtcToLocal(selectedFinding.applied_at)}
                      {selectedFinding.decision_by_name && ` ${t('by')} ${selectedFinding.decision_by_name}`}
                    </p>
                  )}
                </div>
              )}

              {/* Suggested / applied redline or addition — at the BOTTOM, right above decision buttons */}
              {selectedFinding.suggested_redline && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                      {selectedFinding.decision
                        ? (selectedFinding.action_type === 'add_after' ? t('Addition') : t('Redline'))
                        : (selectedFinding.action_type === 'add_after' ? t('Suggested Addition') : t('Suggested Redline'))}
                    </h4>
                    {showModifyInput && (
                      <button
                        onClick={() => { setShowModifyInput(false); setModifiedText(''); }}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      >
                        {t('Cancel editing')}
                      </button>
                    )}
                  </div>
                  {selectedFinding.action_type === 'add_after' && !selectedFinding.decision && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 mb-2 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                      {t('New text to be added after the referenced section')}
                    </p>
                  )}
                  {showModifyInput ? (
                    <textarea
                      value={modifiedText}
                      onChange={(e) => setModifiedText(e.target.value)}
                      className={`w-full border rounded-lg p-4 text-sm leading-relaxed resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary-400 ${
                        selectedFinding.action_type === 'add_after'
                          ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-900/20 text-gray-700 dark:text-gray-300'
                          : 'border-blue-300 dark:border-blue-700 bg-blue-50/30 dark:bg-blue-900/10 text-gray-700 dark:text-gray-300'
                      }`}
                      rows={5}
                    />
                  ) : (
                    <div className={`border rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed ${
                      selectedFinding.action_type === 'add_after'
                        ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30'
                    }`}>
                      {selectedFinding.suggested_redline}
                    </div>
                  )}
                </div>
              )}

              {/* Decision info from other collaborators */}
              {selectedFinding.decision && selectedFinding.decision_by_name && selectedFinding.decision !== 'modified' && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800 rounded-lg px-3 py-2">
                  <PiCheckCircleLight className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {formatLabel(selectedFinding.decision)} by <strong>{selectedFinding.decision_by_name}</strong>
                    {selectedFinding.decision_at && (
                      <span className="text-gray-400 ml-1">
                        {formatUtcToLocal(selectedFinding.decision_at)}
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Decision buttons */}
              {isProcessing ? (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-blue-500 dark:text-blue-400">
                  <PiSpinnerGapLight className="w-4 h-4 animate-spin" />
                  <span>{t('Analysis in progress — decisions can be made once processing is complete')}</span>
                </div>
              ) : decisionsLocked ? (
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400">
                  <PiLockKeyLight className="w-4 h-4" />
                  <span>{t('Decisions are locked — this review has been finalized')}</span>
                </div>
              ) : selectedFinding.decision ? (
                /* Already decided — show only undo */
                <div className="flex items-center gap-2.5 pt-2 border-t border-gray-100 dark:border-gray-800">
                  <span className={clsx(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium',
                    selectedFinding.decision === 'accepted' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                    selectedFinding.decision === 'rejected' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                    selectedFinding.decision === 'modified' ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                    selectedFinding.decision === 'escalated' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                    'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                  )}>
                    {selectedFinding.decision === 'accepted' && <PiCheckCircleLight className="w-3.5 h-3.5" />}
                    {selectedFinding.decision === 'rejected' && <PiXCircleLight className="w-3.5 h-3.5" />}
                    {selectedFinding.decision === 'modified' && <PiPencilSimpleLight className="w-3.5 h-3.5" />}
                    {selectedFinding.decision === 'escalated' && <PiArrowUpRightLight className="w-3.5 h-3.5" />}
                    {formatLabel(selectedFinding.decision)}
                  </span>
                  <Button
                    onClick={handleUndoDecision}
                    variant='destructive'
                    size='small'
                    title={t('Undo decision and return to pending')}
                    icon={<PiArrowCounterClockwiseLight className="w-3.5 h-3.5" />}
                  >
                    {t('Undo')}
                  </Button>
                </div>
              ) : (
              /* No decision yet — show decision buttons */
              <div className="flex items-center gap-2.5 pt-2 border-t border-gray-100 dark:border-gray-800 flex-wrap" data-tour-id="review-finding-decisions">
                <Button
                  variant="primary"
                  size="small"
                  icon={<PiCheckCircleLight className="w-4 h-4" />}
                  onClick={() => handleDecision('accepted')}
                >
                  {t('Accept')}
                </Button>
                <Button
                  variant="destructive"
                  size="small"
                  icon={<PiXCircleLight className="w-4 h-4" />}
                  onClick={() => handleDecision('rejected')}
                >
                  {t('Reject')}
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  icon={<PiPencilSimpleLight className="w-4 h-4" />}
                  onClick={() => {
                    if (showModifyInput && modifiedText.trim()) {
                      handleDecision('modified');
                    } else {
                      setShowModifyInput(!showModifyInput);
                      if (!modifiedText && selectedFinding.suggested_redline) {
                        setModifiedText(selectedFinding.suggested_redline);
                      }
                    }
                  }}
                >
                  {showModifyInput && modifiedText.trim() ? t('Submit Modification') : t('Modify')}
                </Button>
                <Button
                  variant="outline"
                  size="small"
                  icon={<PiArrowUpRightLight className="w-4 h-4" />}
                  onClick={() => handleDecision('escalated')}
                >
                  {t('Escalate')}
                </Button>
              </div>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <PiFileTextLight className="w-12 h-12 mb-3" />
              <p className="text-sm">{t('Select a finding to review')}</p>
            </div>
          )}
        </div>

        {/* ─── Right side-panel: Activity (slide-in) ────────────── */}
        {activityOpen && reviewUuid && (
          <div className="w-[340px] flex-shrink-0 border-l border-gray-100 dark:border-gray-700/60 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
            {/* Simple header with close button */}
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/60 px-4 py-2.5">
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                <PiClockCounterClockwiseLight className="w-4 h-4 text-gray-400" />
                {t('Activity')}
              </div>
              <button
                onClick={() => setActivityOpen(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <PiCaretRightLight className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>

            {/* Activity log */}
            <div className="flex-1 overflow-y-auto custom-scrollbar-main" style={{ maxHeight: `calc(100vh - ${145 + bannerOffset}px)` }}>
              <ReviewAuditLog sessionUuid={reviewUuid} onFindingSelect={handleFindingSelectFromLog} />
            </div>
          </div>
        )}
      </div>
    </DetailFrame>

    <FeatureTour
      steps={reviewSessionTourSteps}
      run={isTourRunning}
      onCallback={handleTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
  </>);
};

export default ReviewSessionDetail;
